import React, { useState, useRef, useEffect } from "react";
import { Map, TileLayer, Marker, Polyline } from "react-leaflet";
import L from "leaflet";
import ReactDOMServer from "react-dom/server";
import Swal from "sweetalert2";
import axios from "axios";
import "leaflet/dist/leaflet.css";
import {
  FiMaximize, FiMinimize, FiMap, FiMapPin, FiFlag
} from "react-icons/fi";
import { MdOutlineDirectionsCarFilled } from "react-icons/md";

const TrackingRoute = ({ locations = [], routeData, routeVehicle }) => {
  const mapRef = useRef(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [routeCoords, setRouteCoords] = useState([]);

  const routeInit = routeData?.start ? {
    lat: parseFloat(routeData.start.lat),
    lng: parseFloat(routeData.start.long),
  } : { lat: 0, lng: 0 };

  const routeEnd = routeData?.end ? {
    lat: parseFloat(routeData.end.lat),
    lng: parseFloat(routeData.end.long),
  } : { lat: 0, lng: 0 };

  const parsedCoordinates = locations.map(loc => ({
    lat: parseFloat(loc.lat),
    lng: parseFloat(loc.long),
    temp: loc.temperatura,
  }));

  const pathStops = routeData?.path?.map((p) => ({
    coord: [parseFloat(p.way_point.lat), parseFloat(p.way_point.long)],
    name: p.way_point.name,
    order: p.order,
  })) || [];

  const lastCoord = parsedCoordinates[parsedCoordinates.length - 1] || routeInit;

  useEffect(() => {
    const fetchRoute = async () => {
      if (parsedCoordinates.length < 2) return;
      const apiKey = "5b3ce3597851110001cf624896403128bb33428ea39b660ab641a28f";
      const coordsForAPI = parsedCoordinates.map(({ lng, lat }) => [lng, lat]);

      try {
        setLoading(true);
        const response = await axios.post(
          "https://api.openrouteservice.org/v2/directions/driving-car/geojson",
          { coordinates: coordsForAPI },
          {
            headers: {
              Authorization: apiKey,
              "Content-Type": "application/json",
            },
          }
        );

        const routePoints = response.data.features[0].geometry.coordinates.map(
          ([lng, lat]) => [lat, lng]
        );

        setRouteCoords(routePoints);
        setLoading(false);
        setTimeout(() => fitAll(), 300);
      } catch (error) {
        setLoading(false);
        Swal.fire({
          icon: "error",
          title: "Erro ao buscar rota",
          text: error.message || "Erro desconhecido",
        });
      }
    };

    fetchRoute();
  }, [locations]);

  useEffect(() => {
    if (mapRef.current) {
      setTimeout(() => {
        mapRef.current.leafletElement.invalidateSize();
      }, 300);
    }
  }, [fullscreen]);

  const iconDiv = (iconElement, bgColor = "#3498db", label = "") =>
    L.divIcon({
      html: ReactDOMServer.renderToString(
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              backgroundColor: bgColor,
              padding: "6px",
              borderRadius: "50%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              color: "#fff",
              boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
            }}
          >
            {iconElement}
          </div>
          {label && (
            <div style={{ fontSize: 10, color: "#333", fontWeight: "bold" }}>{label}</div>
          )}
        </div>
      ),
      className: "",
      iconSize: [30, 42],
      iconAnchor: [15, 42],
    });

  const flyTo = (latlng, zoom = 17) => {
    if (mapRef.current) {
      mapRef.current.leafletElement.flyTo(latlng, zoom, { duration: 1.2 });
    }
  };

  const fitAll = () => {
    if (mapRef.current) {
      const allCoords = [
        ...parsedCoordinates.map(p => [p.lat, p.lng]),
        [routeInit.lat, routeInit.lng],
        [routeEnd.lat, routeEnd.lng],
        ...pathStops.map(p => p.coord),
      ];
      const bounds = L.latLngBounds(allCoords);
      mapRef.current.leafletElement.fitBounds(bounds, { padding: [20, 20] });
    }
  };

  const buttons = [
    { label: "Início", color: "#2ecc71", coord: [routeInit.lat, routeInit.lng] },
    { label: "Último", color: "#2980b9", coord: [lastCoord.lat, lastCoord.lng] },
    { label: "Fim", color: "#e74c3c", coord: [routeEnd.lat, routeEnd.lng] },
    ...pathStops.map((p) => ({ label: p.name, color: "#f39c12", coord: p.coord })),
  ];

  return (
    <div style={{ position: fullscreen ? "fixed" : "relative", top: 0, left: 0, height: fullscreen ? "100vh" : "60vh", width: "100%", zIndex: fullscreen ? 999 : "auto" }}>
      {/* Card do veículo */}
      {routeVehicle && (
        <div style={{
          position: "absolute",
          top: 20,
          left: 20,
          background: "white",
          borderRadius: 10,
          padding: 16,
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          zIndex: 999
        }}>
          <h3 style={{ margin: 0, fontSize: 16, color: "#2c3e50" }}>{routeVehicle.name}</h3>
          <p style={{ margin: 4, fontSize: 14 }}>Veículo: {routeVehicle.vehicle.model} - {routeVehicle.vehicle.plate}</p>
          <p style={{ margin: 4, fontSize: 14 }}>Início: {routeVehicle.start.name}</p>
          <p style={{ margin: 4, fontSize: 14 }}>Fim: {routeVehicle.end.name}</p>
        </div>
      )}

      {/* Botão fullscreen */}
      <div
        onClick={() => setFullscreen(!fullscreen)}
        style={{
          position: "absolute",
          top: 20,
          right: 20,
          zIndex: 1000,
          background: "white",
          borderRadius: 8,
          padding: 6,
          cursor: "pointer",
          boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
        }}
      >
        {fullscreen ? <FiMinimize size={20} /> : <FiMaximize size={20} />}
      </div>

      {/* Botões de navegação */}
      <div style={{ position: "absolute", bottom: 20, left: 20, display: "flex", gap: 10, zIndex: 999, flexWrap: "wrap" }}>
        {buttons.map((btn, idx) => (
          <button
            key={idx}
            onClick={() => flyTo(btn.coord)}
            style={{
              backgroundColor: btn.color,
              border: "none",
              borderRadius: 20,
              padding: "8px 14px",
              color: "#fff",
              fontWeight: "bold",
              cursor: "pointer",
              boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
            }}
          >
            {btn.label}
          </button>
        ))}
      </div>

      {/* Mapa */}
      <Map ref={mapRef} center={[routeInit.lat, routeInit.lng]} zoom={15} style={{ height: "100%", width: "100%" }}>
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <Marker position={[routeInit.lat, routeInit.lng]} icon={iconDiv(<FiMap />, "#2ecc71", "Início")} />
        <Marker position={[routeEnd.lat, routeEnd.lng]} icon={iconDiv(<FiFlag />, "#e74c3c", "Fim")} />
        <Marker position={[lastCoord.lat, lastCoord.lng]} icon={iconDiv(<FiMapPin />, "#2980b9", "Último")} />
        {pathStops.map((point, idx) => (
          <Marker
            key={idx}
            position={point.coord}
            icon={iconDiv(<MdOutlineDirectionsCarFilled />, "#f39c12", "Parada")}
          />
        ))}
        {parsedCoordinates.map((point, idx) => (
          <Marker
            key={idx}
            position={[point.lat, point.lng]}
            icon={iconDiv(
              <span style={{ fontSize: 10 }}>{point.temp.toFixed(1)}°C</span>,
              "#34495e"
            )}
          />
        ))}
        {routeCoords.length > 0 && <Polyline positions={routeCoords} color="#1E90FF" />}
      </Map>

      {/* Loading */}
      {loading && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 2000,
            background: "rgba(255,255,255,0.9)",
            padding: 20,
            borderRadius: 8,
            boxShadow: "0 0 10px rgba(0,0,0,0.2)",
            fontWeight: "bold",
          }}
        >
          Carregando rota...
        </div>
      )}
    </div>
  );
};

export default TrackingRoute;
